import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  toCategoryLink,
  toLandingPageLink,
  toSubcategoryLink,
} from 'lib/links';
import { landingQueryKeys, offerQueryKeys } from 'lib/queryKeys';
import { isBlackFridayLanding } from 'lib/utils';

import { getCategoriesList } from 'services/category';
import { getLandingsList } from 'services/landing';

const staleOptions = {
  enabled: false,
  refetchOnWindowFocus: false,
  staleTime: Infinity,
};

const useCategoriesWithHighlightedLandings = () => {
  const {
    data: highlightedLandings = [],
    isLoading: isHighlightedLandingsLoading,
  } = useQuery({
    queryKey: landingQueryKeys.highlighted(),
    queryFn: ({ signal }) => getLandingsList({ highlighted: true }, { signal }),
    select: (data) => data.landings,
    ...staleOptions,
  });

  const { data: categories = [], isLoading: isCategoriesLoading } = useQuery({
    queryKey: offerQueryKeys.categories({ includeOfferSelections: true }),
    queryFn: ({ signal }) =>
      getCategoriesList({ selectedCategories: true }, { signal }),
    ...staleOptions,
  });

  const { data: featuredLandings = [], isLoading: isFeaturedLandingsLoading } =
    useQuery({
      queryKey: landingQueryKeys.featured(),
      queryFn: ({ signal }) => getLandingsList({ featured: true }, { signal }),
      select: (data) => data.landings,
      ...staleOptions,
    });

  const categoriesWithHighlightedLandings = useMemo(() => {
    if (
      isCategoriesLoading ||
      isFeaturedLandingsLoading ||
      isHighlightedLandingsLoading ||
      categories?.length === 0 ||
      categories === null
    ) {
      return [];
    }

    const categoriesWithUrl = categories.map((category) => ({
      ...category,
      categoryUrl: toCategoryLink(category.categoryUrl),
      subcategories: category.subcategories.map((s) => ({
        ...s,
        subcategoryUrl: toSubcategoryLink(s.subcategoryParam),
      })),
    }));

    if (featuredLandings.length === 0 || highlightedLandings.length === 0) {
      return categoriesWithUrl;
    }

    if (featuredLandings.some(isBlackFridayLanding)) {
      return categoriesWithUrl.concat(
        highlightedLandings.map(
          ({ landingName, landingSlug, landingType }) => ({
            categoryName: landingName,
            categoryUrl: toLandingPageLink(landingSlug, landingType),
            groupName: 'Black Friday',
            subcategories: [],
          })
        )
      );
    }

    return categoriesWithUrl.concat([
      {
        categoryName: 'Páginas Especiais',
        categoryUrl: '',
        groupName: 'Diversos',
        subcategories: highlightedLandings.map(
          ({ landingName, landingSlug, landingType }) => ({
            subcategoryUrl: toLandingPageLink(landingSlug, landingType),
            subcategoryName: landingName,
          })
        ),
      },
    ]);
  }, [
    categories,
    highlightedLandings,
    featuredLandings,
    isCategoriesLoading,
    isFeaturedLandingsLoading,
    isHighlightedLandingsLoading,
  ]);

  return {
    categoriesWithHighlightedLandings,
    isLoading:
      isCategoriesLoading ||
      isFeaturedLandingsLoading ||
      isHighlightedLandingsLoading,
  };
};

export default useCategoriesWithHighlightedLandings;
